import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';

/**
 * BD-15 — on an engagement that was started from a meeting or a Zoho deal: where it came from, the client's
 * pain points with the quotes they rest on, and where each Zoho stage proposal stands. Renders nothing for an
 * ordinary engagement.
 */

interface Origin {
  from_bridge: boolean;
  link?: { meeting_id: number | null; deal_id: string | null; deal_name: string | null; rate_card_account: string; sources: { artifacts?: number; pain_points?: number; excluded_sensitive?: number; redacted_sentences?: number } };
  pain_points?: { id: number; title: string; detail: string; quote: string; meeting_id: number | null; source_label: string | null }[];
  stage_syncs?: { milestone: string; target_stage: string | null; status: 'proposed' | 'skipped'; reason: string | null; action_id: number | null }[];
}

const MILESTONE: Record<string, string> = { ou_oa_delivered: 'OU/OA deck delivered', sow_sent: 'SOW sent', won: 'Signed / PM handoff' };
const REASON: Record<string, string> = {
  no_deal: 'no Zoho deal is linked',
  unknown_stage: 'that stage is not in your Zoho stage list',
  stage_already_reached: 'the deal is already at or past that stage',
};

const EngagementBridgePanel: React.FC<{ engagementId: number }> = ({ engagementId }) => {
  const [data, setData] = useState<Origin | null>(null);

  useEffect(() => {
    let active = true;
    api.get(`/api/bd/engagement-bridge/engagements/${engagementId}`)
      .then((res) => { if (active) setData(res.data); })
      .catch(() => { if (active) setData(null); });
    return () => { active = false; };
  }, [engagementId]);

  if (!data || !data.from_bridge || !data.link) return null;
  const { link } = data;
  const pain = data.pain_points || [];
  const syncs = data.stage_syncs || [];

  return (
    <div className="border border-blue-100 bg-blue-50/40 rounded-lg p-4 space-y-3" data-testid="engagement-bridge-panel">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">Started from {link.meeting_id ? 'a meeting' : 'a deal'}{link.deal_name ? ` · ${link.deal_name}` : ''}</h4>
        <p className="text-xs text-gray-600">
          Rate card: {link.rate_card_account}
          {link.sources.artifacts != null && ` · ${link.sources.artifacts} document${link.sources.artifacts === 1 ? '' : 's'} pre-loaded`}
          {!!link.sources.excluded_sensitive && ` · ${link.sources.excluded_sensitive} left out as sensitive`}
          {!!link.sources.redacted_sentences && ` · ${link.sources.redacted_sentences} flagged sentence${link.sources.redacted_sentences === 1 ? '' : 's'} removed`}
          {link.meeting_id && <> · <Link to={`/app/bd-meetings/${link.meeting_id}`} className="text-blue-600 hover:underline">Open the meeting</Link></>}
        </p>
      </div>

      {pain.length > 0 && (
        <div data-testid="engagement-bridge-pain">
          <p className="text-xs font-semibold text-gray-700 mb-1">Client pain points, in their words</p>
          <ul className="space-y-2">
            {pain.map((p) => (
              <li key={p.id} className="text-sm text-gray-800">
                {p.title}
                <blockquote className="mt-0.5 border-l-2 border-gray-300 pl-2 text-xs italic text-gray-600">“{p.quote}”{p.source_label && <span className="not-italic text-gray-400"> — {p.source_label}</span>}</blockquote>
              </li>
            ))}
          </ul>
        </div>
      )}

      {pain.length === 0 && (
        <p className="text-xs text-gray-500" data-testid="engagement-bridge-no-pain">
          No quoted pain points came across: the debriefs you chose had no client needs with a quote. The synthesis in the next stage will find them from the documents.
        </p>
      )}

      {syncs.length > 0 && (
        <div data-testid="engagement-bridge-syncs">
          <p className="text-xs font-semibold text-gray-700 mb-1">Zoho stage</p>
          <ul className="space-y-0.5 text-xs text-gray-700">
            {syncs.map((s) => (
              <li key={s.milestone}>
                {MILESTONE[s.milestone] || s.milestone} → {s.target_stage}:{' '}
                {s.status === 'proposed'
                  ? <Link to="/app/business-development?tab=approvals" className="text-amber-700 hover:underline">waiting for your approval</Link>
                  : <span className="text-gray-500">not proposed, {REASON[s.reason || ''] || 'skipped'}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default EngagementBridgePanel;
